import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import apiClient from '../../utils/api';
import { toast } from 'sonner';

const OpeningBalanceBSStock = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ circumference: '', length: '', bore: '', degree: '', key_way: '', nos: '' });

  const fetchEntries = () => {
    apiClient.get('/opening-balance/bs')
      .then(res => setEntries(res.data))
      .catch(() => toast.error('Failed to load entries'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchEntries(); }, []);

  const resetForm = () => {
    setForm({ circumference: '', length: '', bore: '', degree: '', key_way: '', nos: '' });
    setShowForm(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!form.circumference || !form.length || !form.nos) {
      toast.error('Circumference, Length and Nos. are required');
      return;
    }
    const payload = {
      circumference: parseFloat(form.circumference),
      length: parseFloat(form.length),
      bore: form.bore ? parseFloat(form.bore) : null,
      degree: form.degree ? parseFloat(form.degree) : null,
      key_way: form.key_way || null,
      nos: parseInt(form.nos),
    };
    try {
      if (editingId) {
        await apiClient.put(`/opening-balance/bs/${editingId}`, payload);
        toast.success('Entry updated');
      } else {
        await apiClient.post('/opening-balance/bs', payload);
        toast.success('Entry added');
      }
      resetForm();
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    }
  };

  const handleEdit = (entry) => {
    setEditingId(entry.id);
    setForm({
      circumference: String(entry.circumference || ''),
      length: String(entry.length || ''),
      bore: String(entry.bore || ''),
      degree: String(entry.degree || ''),
      key_way: entry.key_way || '',
      nos: String(entry.nos || ''),
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await apiClient.delete(`/opening-balance/bs/${id}`);
      toast.success('Entry deleted');
      fetchEntries();
    } catch (error) {
      console.error('Failed to delete opening balance:', error);
      toast.error('Failed to delete');
    }
  };

  const handleReplaceAll = async () => {
    if (!window.confirm('This will DELETE all existing opening balance entries. You can then add new ones. Continue?')) return;
    try {
      await apiClient.delete('/opening-balance/bs/all');
      toast.success('All entries cleared. Add new stock entries now.');
      fetchEntries();
      resetForm();
      setShowForm(true);
    } catch (error) {
      console.error('Failed to clear opening balance entries:', error);
      toast.error('Failed to clear entries');
    }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900">BS Stock - Opening Balance</h3>
        <div className="flex gap-2">
          {!showForm && (
            <Button variant="outline" size="sm" className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50" onClick={handleReplaceAll}>
              <Trash2 className="h-4 w-4" /> Replace All Stock
            </Button>
          )}
          {!showForm && (
            <Button onClick={() => { resetForm(); setShowForm(true); }} className="flex items-center gap-2" size="sm">
              <Plus className="h-4 w-4" /> Add Entry
            </Button>
          )}
        </div>
      </div>

      {showForm && (
        <div className="p-4 mb-4 bg-slate-50 border border-slate-200 rounded-lg">
          <div className="grid grid-cols-6 gap-3 mb-3">
            <div>
              <Label className="text-xs font-bold">Circumference *</Label>
              <Input type="number" step="0.01" value={form.circumference} onChange={(e) => setForm(p => ({ ...p, circumference: e.target.value }))} className="mt-1 h-9 text-xs bg-white" placeholder="e.g. 450" />
            </div>
            <div>
              <Label className="text-xs font-bold">Length *</Label>
              <Input type="number" step="0.01" value={form.length} onChange={(e) => setForm(p => ({ ...p, length: e.target.value }))} className="mt-1 h-9 text-xs bg-white" placeholder="e.g. 780" />
            </div>
            <div>
              <Label className="text-xs font-bold">Bore</Label>
              <Input type="number" step="0.01" value={form.bore} onChange={(e) => setForm(p => ({ ...p, bore: e.target.value }))} className="mt-1 h-9 text-xs bg-white" placeholder="e.g. 75" />
            </div>
            <div>
              <Label className="text-xs font-bold">Degree</Label>
              <Input type="number" step="0.01" value={form.degree} onChange={(e) => setForm(p => ({ ...p, degree: e.target.value }))} className="mt-1 h-9 text-xs bg-white" placeholder="e.g. 17" />
            </div>
            <div>
              <Label className="text-xs font-bold">Key Way</Label>
              <Input value={form.key_way} onChange={(e) => setForm(p => ({ ...p, key_way: e.target.value }))} className="mt-1 h-9 text-xs bg-white" placeholder="e.g. 8mm" />
            </div>
            <div>
              <Label className="text-xs font-bold">Nos. *</Label>
              <Input type="number" value={form.nos} onChange={(e) => setForm(p => ({ ...p, nos: e.target.value }))} className="mt-1 h-9 text-xs bg-white" placeholder="e.g. 10" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={resetForm}><X className="h-3.5 w-3.5 mr-1" /> Cancel</Button>
            <Button size="sm" onClick={handleSave}><Save className="h-3.5 w-3.5 mr-1" /> {editingId ? 'Update' : 'Save'}</Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-100 border-b border-slate-200">
              <th className="text-left px-4 py-2.5 font-semibold text-slate-700">Circumference</th>
              <th className="text-left px-4 py-2.5 font-semibold text-slate-700">Length</th>
              <th className="text-left px-4 py-2.5 font-semibold text-slate-700">Bore</th>
              <th className="text-left px-4 py-2.5 font-semibold text-slate-700">Degree</th>
              <th className="text-left px-4 py-2.5 font-semibold text-slate-700">Key Way</th>
              <th className="text-right px-4 py-2.5 font-semibold text-slate-700">Nos.</th>
              <th className="text-center px-4 py-2.5 font-semibold text-slate-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400">No opening balance entries</td></tr>
            ) : entries.map((e) => (
              <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5">{e.circumference}</td>
                <td className="px-4 py-2.5">{e.length}</td>
                <td className="px-4 py-2.5">{e.bore || '-'}</td>
                <td className="px-4 py-2.5">{e.degree || '-'}</td>
                <td className="px-4 py-2.5">{e.key_way || '-'}</td>
                <td className="px-4 py-2.5 text-right font-semibold">{e.nos}</td>
                <td className="px-4 py-2.5 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleEdit(e)} title="Edit">
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(e.id)} title="Delete">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-400 mt-3">These entries are added to Stock &rarr; Base Shell as opening inventory.</p>
    </div>
  );
};

export default OpeningBalanceBSStock;
